import { decodeSidecarBinary } from './sidecarBinary';
import { fetchModel } from './streamBase';
import SlotQueue, { demandOf } from './slotQueue';
import fetchLedger, { shareInFlight } from './fetchLedger';

// ManifestLoader
// Fetches the top-level manifest and per-tile sidecar JSONs. Both go
// through whatever URL the caller provides — could be an Express proxy
// route (auth-gated) or a direct R2/CDN URL with public access. This
// component doesn't care; it just turns URLs into JSON.
//
// In-flight dedupe ensures that two visible tiles asking for the same
// sidecar at the same instant only trigger one HTTP request.
//
// Both manifest.json.gz and tiles/*.json.gz are served by R2 with
// Content-Encoding: gzip, so res.json() resolves with already-decoded
// JSON; no client-side gunzip needed.
//
// === IDB persistence (optional) ===
//
// When the caller passes `blobCache` (an IDBBlobCache instance), the
// raw decoded JSON bytes for both manifest and sidecars are cached by
// URL across sessions. Manifest is ~30 KB and sidecars are ~14 KB
// each × ~1100 tiles per project, so a fresh reload of the SAME
// project previously refetched ~16 MB of small JSON over the wire.
// With persistence, a returning user pays only the parse cost.
//
// The cache key is the absolute URL the caller built, which already
// embeds the projectId — so two distinct projects get independent
// cache entries even if their tile IDs happen to collide.

// Matched to GLBFetcher (96).
const DEFAULT_MAX_CONCURRENT_FETCHES = 96;

const MAX_RETRIES = 3;
const RETRY_BACKOFF_MS = [250, 500, 1000];

function _isTransientFetchError(err) {
  if (!err) return false;
  if (err.name === 'TypeError') return true;
  const msg = String(err.message || '');
  if (msg.includes('HTTP2_PROTOCOL_ERROR')) return true;
  if (msg.includes('ERR_NETWORK')) return true;
  if (msg.includes('Failed to fetch')) return true;
  // 5xx surface as 'ManifestLoader manifest 5xx' / 'ManifestLoader sidecar 5xx'.
  if (/^ManifestLoader \w+ 5\d\d/.test(msg)) return true;
  return false;
}

export default class ManifestLoader {
  constructor({
    manifestUrl, sidecarUrlBuilder, blobCache = null,
    maxConcurrentFetches = DEFAULT_MAX_CONCURRENT_FETCHES,
  }) {
    if (!manifestUrl) throw new Error('ManifestLoader: manifestUrl required');
    if (!sidecarUrlBuilder) {
      throw new Error('ManifestLoader: sidecarUrlBuilder required');
    }
    this.manifestUrl = manifestUrl;
    this.sidecarUrlBuilder = sidecarUrlBuilder;
    this.blobCache = blobCache;
    this.inFlight = new Map();   // url -> Promise<json>
    this.sidecars = new Map();   // tileId -> sidecar JSON

    // Per-instance semaphore for sidecar + manifest fetches. Same
    // direct-handoff pattern as GLBFetcher — release transfers the
    // slot to the next waiter without dipping the active count.
    this.maxConcurrentFetches = maxConcurrentFetches;
    this._activeFetches = 0;
    // Ranked like the fragment fetcher's (slotQueue): a tile about to be
    // drawn outranks the background prefetch of every other sidecar.
    this._fetchQueue = new SlotQueue();
    this._totalFetches = 0;
    this._totalRetries = 0;
    this._totalFailures = 0;

    // Stats so callers can log/observe IDB hit rate. Useful for
    // confirming the cache is doing anything in production.
    this.stats = { manifestHits: 0, manifestMisses: 0,
                   sidecarHits: 0, sidecarMisses: 0 };
  }

  static freshUrl(url, now = Date.now()) {
    const u = String(url || '');
    return `${u}${u.includes('?') ? '&' : '?'}fresh=${now}`;
  }

  _acquireSlot(priority = 0) {
    if (this._closed) { const err = new Error('cancelled: viewer closed'); err.cancelled = true; return Promise.reject(err); }
    if (this._activeFetches < this.maxConcurrentFetches) {
      this._activeFetches++;
      return Promise.resolve();
    }
    const demand = priority && typeof priority === 'object' ? priority : null;
    return new Promise((resolve, reject) => this._fetchQueue.push(
      resolve, demand ? demand.rank : priority, { reject, isStale: demand ? demand.isStale : null }
    ));
  }

  // The viewer is gone: cancel everything waiting for a slot and refuse
  // new work. Downloads already running finish and are ignored.
  close() {
    this._closed = true;
    if (this._fetchQueue) this._fetchQueue.clear();
  }

  _releaseSlot() {
    const next = this._fetchQueue.length > 0 ? this._fetchQueue.shift() : undefined;
    if (next) next();
    else this._activeFetches--;
  }

  // Re-rank the sidecars still waiting (the camera moved).
  reprioritize() { return this._fetchQueue.reprioritize(); }

  // A second requester for a sidecar already on its way: it joins the
  // download's demand, and the queue re-reads ranks so a tile the camera
  // now needs is not left behind the background prefetch that asked first.
  _join(pending, priority) {
    if (pending && pending.waiters && priority !== undefined) {
      pending.waiters.push(priority);
      this._fetchQueue.reprioritize();
    }
    return pending;
  }

  // Network fetch wrapped by the semaphore + bounded retry. Returns
  // the response body as text (callers JSON.parse + cache). `tag` is
  // the label inserted into thrown error messages so transient-error
  // detection can match by prefix.
  // Binary sidecar (sidecarFormat 2): same in-flight dedupe and blob
  // cache as loadSidecar, but the bytes are decoded by sidecarBinary.
  //
  // NOT kept in memory once decoded (2026-09-18). The decoded form is one
  // object and one Float32Array view per instance, and the raw buffer
  // carries the uid table and header too: on SpaceX, 1,459,547 rows across
  // 1,377 tiles held for the session beside the tile registry's own row
  // for each. The tile registry keeps what materialisation needs; a tile
  // reloaded after an unload reads its bytes back from the IndexedDB blob
  // cache (or the network) and decodes again.
  async loadSidecarBinary(tileId, relativeUrl, priority = 0) {
    if (this.sidecars.has(tileId)) return this._cachedSidecar(tileId);
    const url = this.sidecarUrlBuilder(tileId, relativeUrl);
    const pending = this.inFlight.get(url);
    if (pending) return this._join(pending, priority);
    const waiters = [priority];
    const demand = demandOf(waiters);
    const promise = (async () => {
      let buf = null;
      if (this.blobCache && !this._skipBlobCacheForSidecars) {
        try {
          const cached = await this.blobCache.get(url);
          if (cached instanceof ArrayBuffer && cached.byteLength > 20) { buf = cached; this.stats.sidecarHits++; }
        } catch (e) { /* fall back to network */ }
      }
      if (!buf) {
        this.stats.sidecarMisses++;
        buf = await this._fetchBufferWithRetry(url, 'sidecar.bin', undefined, demand);
        if (this.blobCache) void this.blobCache.put(url, buf);
      }
      const decoded = decodeSidecarBinary(buf);
      this.stats.binarySidecars = (this.stats.binarySidecars || 0) + 1;
      return decoded;
    })();
    promise.waiters = waiters;
    this.inFlight.set(url, promise);
    try { return await promise; } finally { this.inFlight.delete(url); }
  }

  // Bytes at a URL the caller already built, with the same retry and IDB
  // caching the sidecars get. The merged singleton meshes are addressed
  // per tile and level of detail rather than by tile id, so they do not
  // fit loadSidecar's one-entry-per-tile cache.
  async loadBinary(url, tag = 'merge') {
    const pending = this.inFlight.get(url);
    if (pending) return pending;
    const promise = (async () => {
      if (this.blobCache && !this._skipBlobCacheForSidecars) {
        try {
          const cached = await this.blobCache.get(url);
          if (cached instanceof ArrayBuffer && cached.byteLength > 20) return cached;
        } catch (e) { /* fall back to network */ }
      }
      const buf = await this._fetchBufferWithRetry(url, tag);
      if (this.blobCache) void this.blobCache.put(url, buf);
      return buf;
    })();
    this.inFlight.set(url, promise);
    try { return await promise; } finally { this.inFlight.delete(url); }
  }

  // One request per URL in flight across the viewer (fetchLedger).
  _fetchBufferWithRetry(url, tag, fetchOpts, priority = 0) {
    return shareInFlight(url, () => this._fetchBufferWithRetryOnce(url, tag, fetchOpts, priority));
  }

  async _fetchBufferWithRetryOnce(url, tag, fetchOpts, priority = 0) {
    let lastErr = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      await this._acquireSlot(priority);
      let begun = false;
      let ok = false;
      let got = 0;
      try {
        this._totalFetches++;
        fetchLedger.begin(url, attempt > 0);
        begun = true;
        const res = await fetchModel(url, { mode: 'cors', ...(fetchOpts || {}) }, this.modelsHost);
        if (!res.ok) {
          const err = new Error(`ManifestLoader ${tag} ${res.status} for ${url}`);
          if (res.status >= 400 && res.status < 500) { this._totalFailures++; throw err; }
          throw err;
        }
        const body = await res.arrayBuffer();
        ok = true;
        got = body.byteLength;
        return body;
      } catch (err) {
        lastErr = err;
        const transient = _isTransientFetchError(err);
        if (!transient || attempt === MAX_RETRIES) { this._totalFailures++; throw err; }
        this._totalRetries++;
      } finally {
        if (begun) fetchLedger.end(url, ok, got);
        this._releaseSlot();
      }
      const base = RETRY_BACKOFF_MS[attempt] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
      const jitter = Math.floor(Math.random() * 100);
      await new Promise(r => setTimeout(r, base + jitter));
    }
    throw lastErr;
  }

  // One request per URL in flight across the viewer (fetchLedger).
  _fetchTextWithRetry(url, tag, fetchOpts, priority = 0) {
    return shareInFlight(`${url}#text`, () => this._fetchTextWithRetryOnce(url, tag, fetchOpts, priority));
  }

  async _fetchTextWithRetryOnce(url, tag, fetchOpts, priority = 0) {
    let lastErr = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      await this._acquireSlot(priority);
      let begun = false;
      let ok = false;
      let got = 0;
      try {
        this._totalFetches++;
        fetchLedger.begin(url, attempt > 0);
        begun = true;
        const res = await fetchModel(url, { mode: 'cors', ...(fetchOpts || {}) }, this.modelsHost);
        if (!res.ok) {
          const err = new Error(`ManifestLoader ${tag} ${res.status} for ${url}`);
          if (res.status >= 400 && res.status < 500) {
            this._totalFailures++;
            throw err;
          }
          throw err;
        }
        const body = await res.text();
        ok = true;
        got = body.length;
        return body;
      } catch (err) {
        lastErr = err;
        const transient = _isTransientFetchError(err);
        if (!transient || attempt === MAX_RETRIES) {
          this._totalFailures++;
          throw err;
        }
        this._totalRetries++;
      } finally {
        if (begun) fetchLedger.end(url, ok, got);
        this._releaseSlot();
      }
      const base = RETRY_BACKOFF_MS[attempt] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
      const jitter = Math.floor(Math.random() * 100);
      await new Promise(r => setTimeout(r, base + jitter));
    }
    throw lastErr;
  }

  // Parse cached ArrayBuffer back to JSON. Defensive: a corrupt entry
  // returns null so the caller falls through to a network fetch.
  _decodeCachedJson(buf) {
    if (!buf || !(buf instanceof ArrayBuffer)) return null;
    try {
      const text = new TextDecoder('utf-8').decode(buf);
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  // Encode JSON to ArrayBuffer for IDB. We cache the raw post-
  // gzip-decoded bytes so cache hits skip BOTH the network and the
  // JSON.stringify round-trip.
  _encodeForCache(text) {
    try { return new TextEncoder().encode(text).buffer; }
    catch (e) { return null; }
  }

  async loadManifest() {
    // Manifest is NEVER served from IDB/HTTP cache. Two reasons:
    //   1. It changes on every export — caching it stalely makes
    //      every downstream fetch (tiles, fragments) line up against
    //      the WRONG content set even though their URLs still resolve.
    //   2. It's the source of truth for cache-bust tokens used by
    //      sidecar + fragment URLs (see below). Reading a stale
    //      manifest defeats the whole automated invalidation chain.
    //
    // `cache: 'no-cache'` tells the HTTP layer to revalidate with the
    // origin (sends If-None-Match using R2's ETag) on every fetch;
    // 304 returns reuse the in-memory body without network bytes,
    // 200 returns are fresh content. We also bypass `this.blobCache`
    // for manifest reads + writes.
    this.stats.manifestMisses++;
    //
    // The query is unique per load for the same reason at the edge: from
    // 2026-09-16 (prod c7f3eb41) until the server fix, /stream sent
    // manifests as immutable and Cloudflare kept them. A unique URL
    // never matches those entries, so a republish shows at once.
    const text = await this._fetchTextWithRetry(
      ManifestLoader.freshUrl(this.manifestUrl), 'manifest', { cache: 'no-cache' });
    const manifest = JSON.parse(text);

    // Derive an export-version token from manifest content so the
    // sidecar + fragment URLs auto-bust their IDB cache entries on
    // a fresh export. projectName already embeds the plugin-side
    // export timestamp (`Project Project_26_05_25_13_33_31_050760`),
    // which changes on every re-export — exactly the signal we need.
    // Fall back to a content-derived signature if the format ever
    // changes so the chain never silently breaks.
    this.exportVersion = this._deriveExportVersion(manifest);
    // Tiles of an export copied into the model bucket come from
    // models.bidlight.com (streamBase.fetchModel). The manifest itself
    // was fetched before this is known, and always goes through /stream.
    this.modelsHost = !!manifest.modelsBucket;

    // Belt-and-suspenders cache invalidation. URL-based `?v=` busting
    // can be defeated by (a) a stale dev-server chunk that doesn't
    // append the param, (b) a service worker that strips/normalizes
    // querystrings before serving from its own cache, (c) any IDB key
    // canonicalization that ignores the query. Even though our code
    // doesn't do any of these things, the symptom — `totalFetches=0`
    // while `totalParses=325` on a fresh export — keeps repeating. So
    // we add a second layer: stash exportVersion in localStorage keyed
    // by manifest URL; if the just-loaded manifest's version differs
    // from what we saw last time we hit this URL, NUKE the entire IDB
    // blob cache. The next reads then MUST fetch from R2, which is
    // the source of truth.
    try {
      const storageKey = 'bidlight.exportVersion:' + this.manifestUrl
        .replace(/\?.*$/, '');
      const prev = (typeof localStorage !== 'undefined')
        ? localStorage.getItem(storageKey) : null;
      if (this.exportVersion && prev && prev !== this.exportVersion
          && this.blobCache && typeof this.blobCache.clearAll === 'function') {
        const deleted = await this.blobCache.clearAll();
        // Also skip IDB lookups for sidecars + fragments on the very
        // first reads after the version flip, so any concurrent
        // in-flight cache write can't reintroduce stale bytes.
        this._skipBlobCacheForSidecars = true;
        if (this.blobCache) this.blobCache._skipNextReads = true;
        console.warn(
          '[DIAG-EXPORT] cache-invalidated',
          JSON.stringify({
            reason: 'exportVersion changed',
            previous: prev,
            current: this.exportVersion,
            idbEntriesDeleted: deleted,
          })
        );
      }
      if (this.exportVersion && typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey, this.exportVersion);
      }
    } catch (e) { /* never block manifest load on localStorage/IDB errors */ }

    // [DIAG-EXPORT] One-shot manifest dump — covers the export
    // signature, scene bounds, and per-LOD counts. The user copies
    // this back when geometry looks incomplete; the line tells us
    // whether the exporter produced fewer elements than expected
    // (low instanceCount/fragmentCount/tile count) vs whether the
    // viewer dropped them downstream.
    try {
      const tiles = manifest.tiles || [];
      const tileInstanceSum = tiles.reduce((n, t) => n + (t.instanceCount || 0), 0);
      const fb = manifest.scene?.fullBounds || {};
      const tb = manifest.scene?.trimmedBounds || {};
      console.warn(
        '[DIAG-EXPORT] manifest',
        JSON.stringify({
          url: this.manifestUrl,
          schema: manifest.schemaVersion,
          projectName: manifest.projectName,
          exportVersion: this.exportVersion,
          sceneInstanceCount: manifest.scene?.instanceCount ?? null,
          fragmentCount: manifest.fragmentCount ?? null,
          tiles: tiles.length,
          tileInstanceSum,
          fullBounds: fb,
          trimmedBounds: tb,
        })
      );
    } catch (e) { /* logging must not block */ }

    // Re-wrap sidecarUrlBuilder so all sidecar URLs gain
    // `?v=<exportVersion>` automatically. The wrap is idempotent:
    // calling loadManifest twice on the same instance just produces
    // the same suffix again.
    if (this.exportVersion && !this._sidecarBuilderWrapped) {
      const original = this.sidecarUrlBuilder;
      const versionQS = '?v=' + encodeURIComponent(this.exportVersion);
      this.sidecarUrlBuilder = (tileId, relativeUrl) => {
        const u = original(tileId, relativeUrl);
        return u + (u.includes('?') ? '&v=' + encodeURIComponent(this.exportVersion) : versionQS);
      };
      this._sidecarBuilderWrapped = true;
    }

    return manifest;
  }

  // projectName format from the plugin: "Project Project_<dd>_<mm>_<yy>_<hh>_<mm>_<ss>_<usec>".
  // Strip the literal "Project " prefix + repeated "Project_" header,
  // keep just the timestamp tail. If that's absent (older exports,
  // schema drift), fall back to a stable signature over scene bounds
  // + counts so the token still flips on every export.
  _deriveExportVersion(manifest) {
    // Prefer the field the exporter now stamps (ManifestWriter:
    // "exportVersion"). Everything below this line is recovery for
    // manifests written before it existed.
    //
    // The regex path infers version identity from a DISPLAY NAME, which
    // holds only while that name happens to carry a timestamp in exactly
    // this shape. Rename the project or change the naming convention and
    // it silently returns null - and a null version means the IDB cache
    // never invalidates, so a fresh export looks like it never happened.
    // That is the "totalFetches=0 while totalParses=325" symptom the
    // comment block above is chasing. An explicit field ends the guessing.
    if (manifest && typeof manifest.exportVersion === 'string'
        && manifest.exportVersion) {
      // The version alone misses one case: bytes rewritten UNDER the same
      // stamp (a fragment rebuild, 2026-09-13). The publisher stamps
      // `fragmentsEpoch` - a hash of the fragments index, which changes
      // whenever any fragment's bytes, triangle counts or radius change -
      // so it rides along in the signature. The pipeline hashes do NOT
      // reliably change on such a run and are deliberately left out. The
      // same stamp with the same bytes still yields the same key, so
      // nothing is refetched for nothing; a manifest without the field is
      // the plain version, as before.
      let sig = manifest.exportVersion;
      const epoch = manifest.fragmentsEpoch;
      if (epoch !== undefined && epoch !== null && String(epoch) !== '') sig += '+e' + String(epoch);
      return sig;
    }

    const name = manifest && typeof manifest.projectName === 'string'
      ? manifest.projectName : '';
    const m = name.match(/(\d{2}_\d{2}_\d{2}_\d{2}_\d{2}_\d{2}_\d{1,7})/);
    if (m) return m[1];
    try {
      const sig = JSON.stringify({
        s: manifest?.schemaVersion,
        b: manifest?.scene?.fullBounds,
        f: manifest?.fragmentCount,
        t: (manifest?.tiles || []).length,
      });
      // Simple non-cryptographic hash (djb2) — small + deterministic.
      let h = 5381;
      for (let i = 0; i < sig.length; i++) h = ((h << 5) + h + sig.charCodeAt(i)) | 0;
      return 'sig' + (h >>> 0).toString(36);
    } catch (e) { return null; }
  }

  async loadSidecar(tileId, relativeUrl, priority = 0) {
    if (this.sidecars.has(tileId)) return this._cachedSidecar(tileId);

    const url = this.sidecarUrlBuilder(tileId, relativeUrl);
    const pending = this.inFlight.get(url);
    if (pending) return this._join(pending, priority);

    const waiters = [priority];
    const demand = demandOf(waiters);
    const promise = (async () => {
      // IDB cache-aside, same pattern as GLBFetcher. Misses fall through
      // to network; hits skip both the round-trip and the redirect chain.
      //
      // Skipped entirely when `_skipBlobCacheForSidecars` is set (after
      // a detected exportVersion change). The cache has just been
      // cleared by loadManifest's belt-and-suspenders, but anything
      // racing in flight (or any IDB corruption) would still hand us
      // stale bytes — fetching from network on the first read after a
      // version flip guarantees correctness.
      if (this.blobCache && !this._skipBlobCacheForSidecars) {
        try {
          const cached = await this.blobCache.get(url);
          const json = this._decodeCachedJson(cached);
          if (json) {
            this.stats.sidecarHits++;
            this.sidecars.set(tileId, json);
            return json;
          }
        } catch (e) { /* swallow — fall back to network */ }
      }
      this.stats.sidecarMisses++;

      // Routed through the semaphore so the fire-and-forget
      // prefetchAllSidecars() doesn't blast 1000+ concurrent fetches.
      const text = await this._fetchTextWithRetry(url, 'sidecar', undefined, demand);
      if (this.blobCache) {
        const buf = this._encodeForCache(text);
        if (buf) void this.blobCache.put(url, buf);
      }
      const json = JSON.parse(text);
      // [DIAG-EXPORT] per-tile sidecar summary — surfaces how many
      // instances + fragmentUrls each tile carries + any hashes that
      // reference nothing (would render as invisible holes in the
      // tile).
      try {
        const instances = json.instances || [];
        const urls = json.fragmentUrls || {};
        const urlKeys = Object.keys(urls);
        let unmapped = 0;
        for (const inst of instances) {
          const h = inst.h || inst.hash;
          if (h && !urls[h]) unmapped++;
        }
        console.warn(
          '[DIAG-EXPORT] sidecar',
          JSON.stringify({
            tileId,
            instances: instances.length,
            uniqueFragmentUrls: urlKeys.length,
            instancesWithUnmappedHash: unmapped,
          })
        );
      } catch (e) { /* logging must not block */ }
      this.sidecars.set(tileId, json);
      return json;
    })().finally(() => {
      // Always drop the in-flight entry — success OR failure. Without
      // this, a terminal sidecar failure permanently poisons the URL:
      // subsequent loadSidecar() calls return the rejected promise
      // forever (until page reload). With it, callers can retry.
      this.inFlight.delete(url);
    });

    promise.waiters = waiters;
    this.inFlight.set(url, promise);
    return promise;
  }

  hasSidecar(tileId) { return this.sidecars.has(tileId); }
  getSidecar(tileId) { return this.sidecars.has(tileId) ? this._cachedSidecar(tileId) : null; }

  // A cached sidecar as callers expect it: JSON sidecars are cached parsed;
  // binary ones are cached as bytes and decoded on the way out.
  _cachedSidecar(tileId) {
    const v = this.sidecars.get(tileId);
    if (v && v.__binary) return decodeSidecarBinary(v.__binary);
    return v;
  }
}